'use client';

import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const steps = [
    {
        number: '01',
        label: 'Capture',
        title: 'Mark what matters',
        description:
            'Tag individual functions, classes, or components for tracking. CapState begins recording every change at the block level from that moment forward.',
        code: `// capstate.config.ts
export default {
  track: [
    "calculatePrice",
    "UserAuthService",
    "PaymentHandler"
  ]
}`,
        icon: 'CameraIcon',
        accent: 'text-primary',
        accentBg: 'bg-primary/10',
        span: 'col-span-2',
        rowSpan: '',
    },
    {
        number: '02',
        label: 'Track',
        title: 'Individual history per block',
        description:
            'Each tracked code block develops its own independent timeline. Changes to other parts of the file don\'t pollute your block\'s history.',
        code: null,
        icon: 'ClockIcon',
        accent: 'text-accent',
        accentBg: 'bg-accent/10',
        span: 'col-span-1',
        rowSpan: 'row-span-2',
    },
    {
        number: '03',
        label: 'Compare',
        title: 'See exactly what changed',
        description:
            'Inspect any two versions of a block side by side. Understand precisely what shifted between implementations.',
        code: `// diff: calculatePrice
- const tax = applyTaxLogic(base);
+ const tax = base * 0.18;
  const discount = getDiscount(base);`,
        icon: 'ArrowsRightLeftIcon',
        accent: 'text-purple-400',
        accentBg: 'bg-purple-400/10',
        span: 'col-span-1',
        rowSpan: '',
    },
    {
        number: '04',
        label: 'Restore',
        title: 'Surgical precision recovery',
        description:
            'One command. The block reverts. Everything else in the file stays exactly as you left it.',
        code: `$ capstate restore calculatePrice@2:41pm
✓ Restored calculatePrice (v2 → v1)
✓ 847 other lines untouched`,
        icon: 'ArrowUturnLeftIcon',
        accent: 'text-primary',
        accentBg: 'bg-primary/10',
        span: 'col-span-1',
        rowSpan: '',
    },
];

const trackingTimeline = [
    { time: '09:14', label: 'Initial impl', dot: 'bg-muted' },
    { time: '11:32', label: 'Added caching', dot: 'bg-muted' },
    { time: '14:41', label: 'Tax logic v1', dot: 'bg-accent' },
    { time: '16:08', label: 'Tax logic v2', dot: 'bg-primary' },
];

export default function CapStateHowItWorks() {
    const sectionRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const cards = entry.target.querySelectorAll<HTMLElement>('.howitworks-card');
                        cards.forEach((card, i) => {
                            setTimeout(() => {
                                card.style.opacity = '1';
                                card.style.transform = 'translateY(0) scale(1)';
                            }, i * 120);
                        });
                    }
                });
            },
            { threshold: 0.1 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="how-it-works" ref={sectionRef} className="py-24 px-6 relative">
            <div className="max-w-7xl mx-auto">
                <div className="max-w-xl mb-16">
                    <p className="section-label mb-4">How It Works</p>
                    <h2 className="text-section-heading font-extrabold text-foreground mb-4">
                        Restore what changed.{' '}
                        <span className="text-gradient-accent italic font-light">Keep what didn&apos;t.</span>
                    </h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                        Four steps from broken code to working code — without touching a single line you
                        didn&apos;t write today.
                    </p>
                </div>

                {/* BENTO GRID AUDIT:
          Array has 4 cards: [Capture cs-2, Track cs-1 rs-2, Compare cs-1, Restore cs-1]
          Row 1: [col 1-2: Capture cs-2 rs-1] [col 3: Track cs-1 rs-2]
          Row 2: [col 1: Compare cs-1 rs-1] [col 2: Restore cs-1 rs-1] [col 3: OCCUPIED by Track]
          Placed 4/4 cards ✓
        */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Card 01: Capture — col-span-2 */}
                    <div
                        className="howitworks-card md:col-span-2 bento-card card-hover-lift"
                        style={{ opacity: 1, transform: 'translateY(0) scale(1)' }}
                    >
                        <div className="flex items-start justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-xl ${steps[0].accentBg} flex items-center justify-center`}>
                                    <Icon name={steps[0].icon as any} size={18} className={steps[0].accent} />
                                </div>
                                <div>
                                    <span className="font-mono-code text-xs text-muted-foreground">{steps[0].number}</span>
                                    <h3 className="font-bold text-foreground">{steps[0].label}</h3>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                            {steps[0].description}
                        </p>
                        {steps[0].code && (
                            <div className="bg-card rounded-xl border border-border p-4 overflow-x-auto">
                                <pre className="code-block text-foreground/80 whitespace-pre-wrap text-xs leading-relaxed">
                                    {steps[0].code}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Card 02: Track — col-span-1 row-span-2 */}
                    <div
                        className="howitworks-card md:row-span-2 bento-card card-hover-lift flex flex-col"
                        style={{ opacity: 1, transform: 'translateY(0) scale(1)' }}
                    >
                        <div className="flex items-center gap-3 mb-5">
                            <div className={`w-9 h-9 rounded-xl ${steps[1].accentBg} flex items-center justify-center`}>
                                <Icon name={steps[1].icon as any} size={18} className={steps[1].accent} />
                            </div>
                            <div>
                                <span className="font-mono-code text-xs text-muted-foreground">{steps[1].number}</span>
                                <h3 className="font-bold text-foreground">{steps[1].label}</h3>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                            {steps[1].description}
                        </p>

                        {/* Visual: Block timeline */}
                        <div className="flex-1 bg-card rounded-xl border border-border p-4">
                            <p className="font-mono-code text-xs text-muted-foreground mb-4">
                                calculatePrice() history
                            </p>
                            <div className="relative">
                                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
                                <div className="space-y-4">
                                    {trackingTimeline.map((entry, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <div
                                                className={`w-3.5 h-3.5 rounded-full shrink-0 mt-0.5 z-10 relative ${entry.dot} ${i === trackingTimeline.length - 1
                                                    ? 'shadow-[0_0_8px_rgba(232,255,71,0.6)]'
                                                    : ''
                                                    }`}
                                            />
                                            <div>
                                                <p className="font-mono-code text-xs text-accent">{entry.time}</p>
                                                <p className="text-xs text-muted-foreground">{entry.label}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card 03: Compare */}
                    <div
                        className="howitworks-card bento-card card-hover-lift"
                        style={{ opacity: 1, transform: 'translateY(0) scale(1)' }}
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-9 h-9 rounded-xl ${steps[2].accentBg} flex items-center justify-center`}>
                                <Icon name={steps[2].icon as any} size={18} className={steps[2].accent} />
                            </div>
                            <div>
                                <span className="font-mono-code text-xs text-muted-foreground">{steps[2].number}</span>
                                <h3 className="font-bold text-foreground">{steps[2].label}</h3>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                            {steps[2].description}
                        </p>
                        {steps[2].code && (
                            <div className="bg-card rounded-xl border border-border p-3 overflow-x-auto">
                                <pre className="code-block text-xs leading-relaxed whitespace-pre-wrap">
                                    {steps[2].code.split('\n').map((line, i) => (
                                        <span
                                            key={i}
                                            className={`block ${line.startsWith('-')
                                                ? 'text-red-400' : line.startsWith('+')
                                                    ? 'text-primary' : 'text-foreground/70'
                                                }`}
                                        >
                                            {line}
                                        </span>
                                    ))}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Card 04: Restore */}
                    <div
                        className="howitworks-card bento-card card-hover-lift"
                        style={{ opacity: 1, transform: 'translateY(0) scale(1)' }}
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-9 h-9 rounded-xl ${steps[3].accentBg} flex items-center justify-center`}>
                                <Icon name={steps[3].icon as any} size={18} className={steps[3].accent} />
                            </div>
                            <div>
                                <span className="font-mono-code text-xs text-muted-foreground">{steps[3].number}</span>
                                <h3 className="font-bold text-foreground">{steps[3].label}</h3>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                            {steps[3].description}
                        </p>
                        {steps[3].code && (
                            <div className="bg-card rounded-xl border border-border p-3 overflow-x-auto">
                                <pre className="code-block text-xs leading-relaxed whitespace-pre-wrap">
                                    {steps[3].code.split('\n').map((line, i) => (
                                        <span
                                            key={i}
                                            className={`block ${line.startsWith('$')
                                                ? 'text-accent' : line.startsWith('✓')
                                                    ? 'text-primary' : 'text-foreground/70'
                                                }`}
                                        >
                                            {line}
                                        </span>
                                    ))}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}